import {
  Component,
  Input,
  OnChanges,
  SimpleChanges,
  ViewChild,
  ElementRef,
  AfterViewInit,
  Output,
  EventEmitter,
} from '@angular/core';
import * as Highcharts from 'highcharts';

export interface GaugePointer {
  value: number;
  color: string;
  length?: number;
  label?: string;
  hideLegends?: boolean;
  formatedValue?: string;
  index?: number;
}

@Component({
  selector: 'piqet-angular-gauge-chart',
  templateUrl: './gauge-chart.component.html',
  styleUrls: ['./gauge-chart.component.scss'],
})
export class GaugeChartComponent implements OnChanges, AfterViewInit {
  @ViewChild('chartContainer', { static: true }) chartContainer!: ElementRef;

  @Input() pointers: GaugePointer[] = [];
  @Input() max = 100;
  @Input() min = 0;
  @Input() majorUnit = 20;
  @Input() title = '';
  @Input() chartId = '';
  @Input() showLegend = false; // New input to control legend visibility
  @Input() legendData: GaugePointer[] = []; // New input for legend data

  @Output() legendClick = new EventEmitter<GaugePointer>(); // New output for legend clicks

  private chart: any; // Using any to avoid type conflicts
  private initialized = false;

  constructor() {}

  ngAfterViewInit(): void {
    this.initialized = true;
    this.createChart();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (this.initialized) {
      if (changes['pointers'] || changes['max'] || changes['min'] || changes['majorUnit']) {
        this.updateChart();
      }
    }
  }

  onLegendClick(pointer: GaugePointer): void {
    this.legendClick.emit(pointer);
  }

  private createChart(): void {
    const options: any = {
      chart: {
        type: 'gauge',
        plotBackgroundColor: null,
        plotBackgroundImage: null,
        plotBorderWidth: 0,
        plotShadow: false,
        height: '200px',
        backgroundColor: 'transparent',
        margin: [10, 10, 10, 10],
        spacing: [10, 10, 10, 10],
      },

      title: {
        text: null,
      },

      credits: {
        enabled: false,
      },

      pane: {
        center: ['50%', '85%'],
        size: '110%',
        startAngle: -90,
        endAngle: 90,
        background: [
          {
            backgroundColor: '#F5F5F5',
            borderColor: '#E0E0E0',
            borderWidth: 1,
            innerRadius: '60%',
            outerRadius: '100%',
            shape: 'arc',
          },
        ],
      },

      exporting: {
        enabled: false,
      },

      tooltip: {
        enabled: true,
        backgroundColor: 'rgba(255, 255, 255, 0.95)',
        borderColor: '#ccc',
        borderRadius: 3,
        borderWidth: 1,
        formatter: function () {
          const point = this as any;
          const seriesName = point.series.name || 'Value';
          const value = point.y || 0;
          // Check if it's Material circularity index (value between 0 and 1)
          const isMaterialCircularity = point.series.yAxis.max === 1;
          if (isMaterialCircularity) {
            const formattedValue = parseFloat(value.toFixed(2)).toString();
            return `<b>${seriesName}:</b> ${formattedValue}`;
          } else {
            return `<b>${seriesName}:</b> ${value.toFixed(1)}%`;
          }
        },
      },

      yAxis: {
        min: this.min,
        max: this.max,
        lineWidth: 0,
        minorTickInterval: null,
        tickAmount: 5, // This will give us 5 ticks: 0, 25, 50, 75, 100
        tickPixelInterval: null,
        tickPositions: this.getTickPositions(), // Explicitly set tick positions
        tickWidth: 1,
        tickPosition: 'inside',
        tickLength: 10,
        tickColor: '#999',
        labels: {
          step: 1,
          rotation: 0,
          distance: 15,
          style: {
            fontSize: '11px',
            fontFamily: 'Arial, sans-serif',
            color: '#666',
          },
          formatter: function (this: any) {
            // Format the label values properly
            const value = this.value;
            if (this.axis.max === 1) {
              // For Material circularity index, show as decimal
              return value.toFixed(1);
            } else {
              // For percentages, show as whole numbers
              return value.toString();
            }
          },
        },
        title: {
          text: null,
        },
      },

      plotOptions: {
        gauge: {
          dataLabels: {
            enabled: false,
          },
          dial: {
            radius: '85%',
            backgroundColor: 'gray',
            baseWidth: 8,
            baseLength: '0%',
            rearLength: '0%',
            topWidth: 1,
          },
          pivot: {
            radius: 5,
            backgroundColor: 'gray',
            borderColor: 'gray',
          },
        },
      },

      series: [],
    };

    this.chart = Highcharts.chart(this.chartContainer.nativeElement, options);
    this.updateSeries();
  }

  private getTickPositions(): number[] {
    // Generate tick positions based on min, max, and major unit
    const positions: number[] = [];
    const step = this.majorUnit;

    // Use a small epsilon for floating point comparison
    const epsilon = 0.0001;

    for (let i = this.min; i <= this.max + epsilon; i += step) {
      // Round to avoid floating point precision issues
      const roundedValue = Math.round(i * 10) / 10;
      positions.push(roundedValue);
    }

    // Ensure max is included if not already
    const lastPosition = positions[positions.length - 1];
    if (Math.abs(lastPosition - this.max) > epsilon) {
      positions.push(this.max);
    }

    return positions;
  }

  private updateChart(): void {
    if (this.chart) {
      // Update y-axis
      this.chart.yAxis[0].update({
        min: this.min,
        max: this.max,
        tickPositions: this.getTickPositions(),
      });

      this.updateSeries();
    }
  }

  private updateSeries(): void {
    if (!this.chart) return;

    // Remove all existing series
    while (this.chart.series.length > 0) {
      this.chart.series[0].remove(false);
    }

    // Add new series for each pointer
    this.pointers
      .filter(pointer => !pointer.hideLegends)
      .forEach((pointer, index) => {
        this.chart.addSeries(
          {
            type: 'gauge',
            name: pointer.label || `Series ${index + 1}`,
            data: [
              {
                y: pointer.value,
                color: pointer.color,
              },
            ],
            dial: {
              backgroundColor: pointer.color,
              borderColor: pointer.color,
              borderWidth: 0,
              baseWidth: 6,
              topWidth: 1,
              radius: '80%',
              rearLength: '0%',
            },
            pivot: {
              backgroundColor: pointer.color,
              borderColor: pointer.color,
              radius: 4,
            },
            tooltip: {
              valueSuffix: '', // Remove the % suffix as we're handling it in the formatter
            },
          },
          false
        );
      });

    this.chart.redraw();
  }

  // Utility method to format tooltip
  private formatTooltip(pointer: GaugePointer): string {
    const value = pointer.formatedValue || `${pointer.value.toFixed(1)}%`;
    return `<b>${pointer.label}:</b> ${value}`;
  }

  // Method to handle pointer visibility
  public updatePointerVisibility(pointerIndex: number, visible: boolean): void {
    if (this.pointers[pointerIndex]) {
      this.pointers[pointerIndex].hideLegends = !visible;
      this.updateSeries();
    }
  }
}
